import React from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import { twMerge } from "tailwind-merge";
import { useSelector } from "react-redux";
import { useLazyListCollectionForContractorQuery } from "store/services/collectionApi";
import { useGetContractorUserDataDetailQuery } from "store/services/contractorUserApi";
import { useAuth } from "hooks";
import { LineChart } from "@mui/x-charts";
import { useNavigate } from "react-router-dom";

export default function ContractorDashboardAnalytics() {
  const navigate = useNavigate();
  const { loggedInUser } = useAuth();
  useSelector((state) => state.auth.user);
  const contractorUserId = loggedInUser?.typeRef;

  const { data: contractorUserData } = useGetContractorUserDataDetailQuery(contractorUserId, {
    skip: !contractorUserId,
  });

  const [fetchFulfillments, { data: fulfillmentRes, isLoading, isFetching, error }] = useLazyListCollectionForContractorQuery();

  React.useEffect(() => {
    if (contractorUserId) {
      console.log("Fetching fulfillments for contractorUser:", contractorUserId);
      fetchFulfillments({ contractorUser: contractorUserId });
    }
  }, [contractorUserId, fetchFulfillments]);

  const [chartData, setChartData] = React.useState({
    xData: [],
    seriesData: [],
  });

  // Fetch collection data
  // React.useEffect(() => {
  //   if (contractorUserId) {
  //     fetchFulfillments({ contractorUser: contractorUserId });
  //   }
  // }, [contractorUserId, fetchFulfillments]);

  // Build chart data
  React.useEffect(() => {
    if (fulfillmentRes && Array.isArray(fulfillmentRes.data)) {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 10);

      const acceptanceCounts = fulfillmentRes.data.reduce((acc, fulfillment) => {
        const acceptedAt = fulfillment.acceptedAt || fulfillment.actualPickupTime;
        const date = acceptedAt ? new Date(acceptedAt) : null;
        if (!date || isNaN(date)) return acc;
        const dateStr = date.toISOString().split("T")[0];
        acc[dateStr] = (acc[dateStr] || 0) + 1;
        return acc;
      }, {});

      const dates = getDates(startDate, today);
      const seriesData = dates.map((date) => {
        const dateStr = date.toISOString().split("T")[0];
        return acceptanceCounts[dateStr] || 0;
      });

      setChartData({ xData: dates, seriesData });
    }
  }, [fulfillmentRes]);

  const getDates = (start, end) => {
    const dates = [];
    let current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  // Aggregate driver and vehicle counts
  const totalDrivers =
    contractorUserData?.contractorProfiles?.reduce(
      (acc, profile) => acc + (profile.driverId?.length || 0),
      0
    ) ?? 0;

  const totalVehicles =
    contractorUserData?.contractorProfiles?.reduce(
      (acc, profile) => acc + (profile.vehicle_id?.length || 0),
      0
    ) ?? 0;

  const driverCard = {
    title: "Your Drivers",
    icon: <PeopleIcon fontSize="large" className="text-blue-500" />,
    items: [
      { label: "Total Drivers", count: totalDrivers, link: "/app/contractor/drivers" },
      { label: "Total Vehicles", count: totalVehicles, link: "/app/contractor/vehicles" },
    ],
    bgColor: "bg-blue-50 dark:bg-blue-900",
  };

  return (
    <Box p={3} m={2}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} display="flex">
          <Paper
            className="rounded-lg p-4"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <Typography variant="h6" mb={2}>
              Collections
            </Typography>
            {isLoading || isFetching ? (
              <Typography>Loading...</Typography>
            ) : error ? (
              <Typography color="error">Error loading data</Typography>
            ) : chartData.xData.length === 0 ? (
              <Typography>No data available</Typography>
            ) : (
              <Box display="flex" justifyContent="center">
                <LineChart
                  xAxis={[
                    {
                      data: chartData.xData,
                      scaleType: "time",
                      valueFormatter: (date) => date.toLocaleDateString(),
                    },
                  ]}
                  series={[
                    {
                      data: chartData.seriesData,
                      area: true,
                    },
                  ]}
                  width={800}
                  height={300}
                />
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            className={twMerge(
              "overflow-hidden shadow-md transition-all duration-300 p-2",
              driverCard.bgColor
            )}
            sx={{ borderRadius: 5, p: 2 }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight="bold" className="text-gray-900 dark:text-white">
                {driverCard.title}
              </Typography>
              {driverCard.icon}
            </Box>
            <Divider />
            <Box mt={2}>
              {driverCard.items.map((item, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="space-between"
                  p={1}
                  className="hover:bg-opacity-50 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-1000"
                  onClick={() => item.link && navigate(item.link)}
                >
                  <Typography variant="body1" color="textSecondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" className="text-gray-900 dark:text-white">
                    {item.count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
